import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiCpu, FiGlobe, FiLayout, FiShield } = FiIcons;

function PricingPage() {
  const plans = [
    {
      name: 'Builder',
      price: '$0',
      description: 'The standard for individuals documenting their work.',
      features: [
        'Automatic Session Tracking',
        'Basic AI Summaries',
        'Public Profile Page',
        '3 Social Integrations'
      ],
      cta: 'Current Plan',
      pro: false
    },
    {
      name: 'Pro',
      price: '$12',
      description: 'Advanced AI and custom branding for serious creators.',
      features: [
        'Deep Semantic Analysis',
        'Custom Page Branding',
        'Unlimited Social Platforms',
        'API Access for Custom Tools',
        'Priority AI Processing'
      ],
      cta: 'Upgrade to Pro',
      pro: true
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="max-w-5xl mx-auto px-4 py-20"
    >
      <div className="text-center mb-16">
        <h1 className="text-4xl md:text-5xl font-black text-gray-900 dark:text-white mb-4">Simple, transparent pricing.</h1>
        <p className="text-xl text-gray-600 dark:text-gray-400">Choose the plan that fits your building style.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {plans.map((plan, i) => (
          <motion.div
            key={plan.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className={`relative p-8 rounded-3xl border-2 ${plan.pro ? 'border-gray-900 dark:border-white shadow-xl' : 'border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-800'}`}
          >
            {plan.pro && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-4 py-1 rounded-full text-xs font-bold">
                MOST POPULAR
              </div>
            )}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{plan.name}</h2>
              <div className="mt-4 flex items-baseline">
                <span className="text-5xl font-black tracking-tight text-gray-900 dark:text-white">{plan.price}</span>
                <span className="ml-1 text-xl font-semibold text-gray-500">/mo</span>
              </div>
              <p className="mt-4 text-gray-500">{plan.description}</p>
            </div>

            <ul className="space-y-4 mb-8">
              {plan.features.map(feature => (
                <li key={feature} className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-300">
                  <div className={`p-1 rounded-full ${plan.pro ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                    <SafeIcon icon={FiCheck} className="w-3 h-3" />
                  </div>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <button className={`w-full py-4 rounded-xl font-bold transition-all ${plan.pro ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:scale-[1.02]' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}`}>
              {plan.cta}
            </button>
          </motion.div>
        ))}
      </div>

      <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8">
        {[
          { icon: FiCpu, label: 'Custom AI Models' },
          { icon: FiGlobe, label: 'Global Edge Sync' },
          { icon: FiLayout, label: 'Page Themes' },
          { icon: FiShield, label: 'Secure Storage' }
        ].map(benefit => (
          <div key={benefit.label} className="text-center">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-3 text-gray-900 dark:text-white">
              <SafeIcon icon={benefit.icon} className="w-6 h-6" />
            </div>
            <p className="text-sm font-bold text-gray-900 dark:text-white">{benefit.label}</p>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export default PricingPage;